import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { ChevronRight, MapPin, ArrowRight, Calendar } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import OpenBadge from "@/components/OpenBadge";
import { useSettings } from "@/contexts/SettingsContext";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { api } from "@/lib/api";

function useHomePage() {
  const [page, setPage] = React.useState(null);
  React.useEffect(() => {
    api.get("/public/pages/home").then((r) => setPage(r.data)).catch(() => {});
  }, []);
  return page;
}

function useMenuPreview() {
  const [menu, setMenu] = React.useState(null);
  React.useEffect(() => {
    api.get("/public/menu").then((r) => setMenu(r.data)).catch(() => {});
  }, []);
  return menu;
}

export default function Home() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { locale } = useLocale();
  const page = useHomePage();
  const menu = useMenuPreview();
  const home = page?.sections?.[locale] || page?.sections?.hr || {};

  // JSON-LD LocalBusiness / CafeOrCoffeeShop
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const hoursDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const openingHoursSpec = (settings?.hours || [])
    .filter((h) => h && !h.closed && h.open && h.close)
    .map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: hoursDays[h.day] || "Monday",
      opens: h.open,
      closes: h.close,
    }));
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "CafeOrCoffeeShop",
    name: settings?.business_name || "King's Café",
    description: pickLocalized(settings?.tagline, locale, ""),
    image: settings?.hero_image_url ? [settings.hero_image_url] : undefined,
    url: origin || undefined,
    telephone: settings?.phone || undefined,
    email: settings?.email || undefined,
    priceRange: "€€",
    servesCuisine: ["Coffee", "Brunch", "European"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Ulica Josipa Šajnovića 2/A",
      addressLocality: "Mursko Središće",
      postalCode: "40315",
      addressCountry: "HR",
    },
    geo: settings?.coordinates && {
      "@type": "GeoCoordinates",
      latitude: settings.coordinates.lat,
      longitude: settings.coordinates.lng,
    },
    openingHoursSpecification: openingHoursSpec.length ? openingHoursSpec : undefined,
    sameAs: [settings?.social?.instagram, settings?.social?.facebook].filter(Boolean),
  };

  // Pick 3 signature items across categories
  const previewItems = React.useMemo(() => {
    if (!menu) return [];
    const cats = menu.categories || [];
    const out = [];
    for (const c of cats) {
      const items = menu.items_by_category?.[c.id] || [];
      if (items[0]) out.push({ ...items[0], category: c });
      if (out.length >= 3) break;
    }
    return out;
  }, [menu]);

  return (
    <PublicLayout
      pageTitle={home.hero_title || "King's Café"}
      pageDescription={home.hero_subtitle}
      testid="home-public-page"
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>
      </Helmet>
      {/* HERO */}
      <section
        className="relative overflow-hidden"
        data-testid="hero-section"
      >
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "radial-gradient(50% 60% at 30% 0%, hsla(43,65%,53%,0.15), transparent 70%), radial-gradient(50% 60% at 90% 90%, hsla(43,65%,53%,0.08), transparent 70%)",
          }}
        />
        <div aria-hidden className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20 sm:pb-28 relative">
          <div className="flex flex-col items-start gap-6">
            <div className="flex items-center gap-3 kc-fade-up kc-fade-up-1">
              <OpenBadge />
              <span className="text-xs text-muted-foreground hidden sm:inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {t("home_page.address_short")}
              </span>
            </div>
            <p className="text-xs uppercase tracking-[0.35em] text-primary/80 kc-fade-up kc-fade-up-1" data-testid="home-hero-eyebrow">
              {t("home_page.hero_eyebrow")}
            </p>
            <h1
              className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] kc-fade-up kc-fade-up-2 max-w-3xl"
              data-testid="home-hero-title"
            >
              {home.hero_title || "King’s Café"}
            </h1>
            <p
              className="text-base sm:text-xl text-muted-foreground max-w-xl kc-fade-up kc-fade-up-3"
              data-testid="home-hero-subtitle"
            >
              {home.hero_subtitle}
            </p>
            <div className="flex items-center gap-4 mt-4 kc-fade-up kc-fade-up-3">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition gold-glow"
                data-testid="home-cta-reserve"
              >
                {home.cta_label || t("common.reserve_table")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border hover:border-primary/40 text-foreground hover:text-primary transition"
                data-testid="home-cta-menu"
              >
                {t("common.view_menu")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-5 gap-10 items-start" data-testid="home-about-section">
        <div className="md:col-span-2">
          <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— 01 / {t("nav.about")}</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-3">
            {home.about_preview_title}
          </h2>
        </div>
        <div className="md:col-span-3 space-y-5">
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {home.about_preview_body}
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
            data-testid="home-about-link"
          >
            {t("home_page.section_about_link")} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* MENU SIGNATURE */}
      <section className="border-y border-border/60 bg-card/30" data-testid="home-menu-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— 02 / {t("nav.menu")}</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-3">{home.menu_teaser_title}</h2>
              <p className="text-muted-foreground mt-3 max-w-md">{home.menu_teaser_body}</p>
            </div>
            <Link
              to="/menu"
              className="hidden sm:inline-flex items-center gap-1 text-primary hover:underline text-sm"
              data-testid="home-menu-link"
            >
              {t("home_page.section_menu_link")} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-5" data-testid="home-menu-items">
            {previewItems.length === 0 && (
              <div className="text-sm text-muted-foreground col-span-3">{t("menu_page.no_items")}</div>
            )}
            {previewItems.map((it) => (
              <div
                key={it.id}
                className="border border-border rounded-xl p-5 bg-background/60 hover:border-primary/40 transition"
                data-testid={`home-menu-item-${it.id}`}
              >
                <p className="text-[11px] uppercase tracking-[0.25em] text-primary/80">
                  {pickLocalized(it.category?.name, locale, "")}
                </p>
                <h3 className="font-display text-xl mt-2">{pickLocalized(it.name, locale, "")}</h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {pickLocalized(it.description, locale, "")}
                </p>
                <div className="mt-4 text-primary text-sm">
                  {t("common.from")} {it.currency === "EUR" ? "€" : it.currency} {Number(it.price).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/menu"
            className="sm:hidden inline-flex items-center gap-1 text-primary hover:underline text-sm mt-6"
          >
            {t("home_page.section_menu_link")} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* EVENTS TEASER */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-10 items-center" data-testid="home-events-section">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— 03 / {t("nav.events")}</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-3">{home.events_teaser_title}</h2>
          <p className="text-muted-foreground mt-5 max-w-md">{home.events_teaser_body}</p>
          <Link
            to="/events"
            className="inline-flex items-center gap-1 text-primary hover:underline text-sm mt-5"
            data-testid="home-events-link"
          >
            {t("home_page.section_events_link")} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="relative">
          <div className="aspect-[4/3] rounded-2xl border border-border bg-gradient-to-br from-primary/15 via-card to-background flex items-center justify-center">
            <Calendar className="w-20 h-20 text-primary/40" strokeWidth={1} />
          </div>
          <div className="absolute -bottom-4 -right-4 text-[10px] uppercase tracking-[0.3em] bg-background border border-border px-3 py-1.5 rounded-full">
            {t("common.coming_soon")}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
